// App «Agentes» del SO Orion — básico de TODA org (2026-10-28).
//
// La misma app en los dos shells: `orion/agentes` (shell Orion) y
// `agentes/mis-agentes` (shell clásico). Orden pensado para usarse, no para leerse:
//  · «Crear agente» siempre arriba (cabecera de la app).
//  · Crear = 2 pasos: elegir plantilla (o desde cero) → ajustar. Las plantillas
//    viven en el primer paso, no al fondo de la página.
//  · Instrucciones compactas (se amplían solo si hace falta).
//  · Editar agentes (PUT /assistants/{id}; antes solo se podía crear y borrar).
// Backend: mcp_django/apps/assistants (via @/modules/assistants/api). AgentsView
// (/chat) sigue igual: esta app no lo toca.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Bot, Loader2, Lock, Maximize2, MessageSquare, Minimize2, Pencil, Plus, Search, Share2, Sparkles, Trash2, Users,
} from 'lucide-react';

import { createAssistant, deleteAssistant, updateAssistant } from '@/modules/assistants/api';
import type { AssistantOut, AssistantVisibility } from '@/modules/assistants/types';
import { OsApp } from '@/modules/orion-shell/OsApp';
import { useAgents } from '@/modules/orion-shell/data';
import { TEMPLATES, type AgentTemplate } from './templates';

const VISIBILITY: Record<AssistantVisibility, { label: string; icon: JSX.Element }> = {
  personal: { label: 'Solo yo', icon: <Lock size={12} aria-hidden /> },
  org: { label: 'Toda la organización', icon: <Users size={12} aria-hidden /> },
  shared: { label: 'Compartido', icon: <Share2 size={12} aria-hidden /> },
};

interface Draft {
  id?: string;
  name: string;
  icon: string;
  description: string;
  instructions: string;
  visibility: AssistantVisibility;
}

const EMPTY: Draft = { name: '', icon: '', description: '', instructions: '', visibility: 'personal' };

type View = { kind: 'list' } | { kind: 'pick' } | { kind: 'edit'; draft: Draft; fromTemplate?: string };

export function AgentsApp() {
  const agents = useAgents();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { moduleKey } = useParams();
  const [sp, setSp] = useSearchParams();
  const [view, setView] = useState<View>(() => (sp.get('nuevo') === '1' ? { kind: 'pick' } : { kind: 'list' }));
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // `?nuevo=1` (Inicio, ⌘K «Crear nuevo agente») abre directo el primer paso.
  useEffect(() => {
    if (sp.get('nuevo') === '1') {
      setView({ kind: 'pick' });
      sp.delete('nuevo');
      setSp(sp, { replace: true });
    }
  }, [sp, setSp]);

  const list = agents.data ?? [];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) => `${a.name} ${a.description ?? ''} ${a.instructions ?? ''}`.toLowerCase().includes(q));
  }, [list, query]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['orion-shell', 'agents'] });

  // En el shell Orion el chat es /m/orion/asistente; en el clásico, /chat.
  const chatPath = (id: string) =>
    moduleKey === 'orion' ? `/m/orion/asistente?nuevo=${Date.now()}&agente=${id}` : `/chat?agente=${id}`;

  const startFromTemplate = (t: AgentTemplate) =>
    setView({ kind: 'edit', fromTemplate: t.title, draft: { ...EMPTY, name: t.name, icon: t.emoji, description: t.summary, instructions: t.instructions } });

  const remove = async (a: AssistantOut) => {
    if (!window.confirm(`¿Eliminar el agente «${a.name}»? Ya no aparecerá en el chat.`)) return;
    setError(null);
    try {
      await deleteAssistant(a.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo eliminar');
    }
  };

  const subtitle = agents.isLoading
    ? 'Cargando…'
    : list.length === 0
      ? 'Especialistas con tus instrucciones, listos en el chat'
      : `${list.length} ${list.length === 1 ? 'agente' : 'agentes'} · especialistas con tus instrucciones`;

  return (
    <div className="mx-auto w-full max-w-6xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <OsApp
        icon={Bot}
        title="Agentes"
        subtitle={subtitle}
        actions={
          view.kind === 'list' ? (
            <button
              type="button"
              onClick={() => setView({ kind: 'pick' })}
              className="os-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-sm font-semibold"
            >
              <Plus size={16} aria-hidden /> Crear agente
            </button>
          ) : null
        }
      >
        {error && <p role="alert" className="mb-3 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>}

        {view.kind === 'pick' && (
          <TemplatePicker
            onPick={startFromTemplate}
            onBlank={() => setView({ kind: 'edit', draft: { ...EMPTY } })}
            onCancel={() => setView({ kind: 'list' })}
          />
        )}

        {view.kind === 'edit' && (
          <AgentEditor
            initial={view.draft}
            fromTemplate={view.fromTemplate}
            onBack={view.draft.id ? undefined : () => setView({ kind: 'pick' })}
            onCancel={() => setView({ kind: 'list' })}
            onSaved={async () => {
              await refresh();
              setView({ kind: 'list' });
            }}
          />
        )}

        {view.kind === 'list' && (
          <>
            {agents.isLoading && (
              <p role="status" className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 size={16} className="animate-spin" aria-hidden /> Cargando agentes…
              </p>
            )}
            {agents.isError && (
              <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">
                No se pudieron cargar los agentes. Intenta de nuevo en un momento.
              </p>
            )}

            {!agents.isLoading && !agents.isError && list.length === 0 && (
              <FirstAgent onPick={startFromTemplate} onMore={() => setView({ kind: 'pick' })} />
            )}

            {list.length > 0 && (
              <>
                {list.length > 6 && (
                  <label className="relative mb-3 block">
                    <span className="sr-only">Buscar agente</span>
                    <Search size={15} aria-hidden className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Buscar por nombre o tarea…"
                      className="h-10 w-full rounded-xl border border-input bg-card pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </label>
                )}
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {filtered.map((a) => (
                    <AgentCard
                      key={a.id}
                      agent={a}
                      onEdit={() =>
                        setView({
                          kind: 'edit',
                          draft: {
                            id: a.id,
                            name: a.name,
                            icon: a.icon ?? '',
                            description: a.description ?? '',
                            instructions: a.instructions ?? '',
                            visibility: a.visibility,
                          },
                        })
                      }
                      onTry={() => navigate(chatPath(a.id))}
                      onDelete={() => remove(a)}
                    />
                  ))}
                </ul>
                {filtered.length === 0 && (
                  <p className="py-6 text-center text-sm text-muted-foreground">Ningún agente coincide con «{query}».</p>
                )}
              </>
            )}
          </>
        )}
      </OsApp>
    </div>
  );
}

// ── Lista ─────────────────────────────────────────────────────

function AgentCard({ agent: a, onEdit, onTry, onDelete }: {
  agent: AssistantOut;
  onEdit: () => void;
  onTry: () => void;
  onDelete: () => void;
}) {
  const vis = VISIBILITY[a.visibility] ?? VISIBILITY.personal;
  const preview = (a.description || a.instructions || '').trim();
  return (
    <li className="orion-card flex min-w-0 flex-col rounded-2xl border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <span aria-hidden className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-xl">
          {a.icon || <Bot size={20} className="text-primary" />}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[15px] font-semibold text-foreground">{a.name}</h3>
          <span className="mt-0.5 inline-flex items-center gap-1 text-[11.5px] text-muted-foreground">
            {vis.icon} {vis.label}
          </span>
        </div>
      </div>
      {preview && <p className="mt-3 line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">{preview}</p>}
      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-4">
        <button
          type="button"
          onClick={onTry}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 text-[13px] font-medium text-primary hover:bg-primary/15"
        >
          <MessageSquare size={14} aria-hidden /> Probar en el chat
        </button>
        {a.is_owner && (
          <>
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium text-foreground hover:bg-muted"
            >
              <Pencil size={14} aria-hidden /> Editar
            </button>
            <button
              type="button"
              onClick={onDelete}
              aria-label={`Eliminar ${a.name}`}
              className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 size={15} aria-hidden />
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function FirstAgent({ onPick, onMore }: { onPick: (t: AgentTemplate) => void; onMore: () => void }) {
  return (
    <div className="py-2">
      <div className="mb-4">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">Crea tu primer agente en un minuto</h2>
        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Un agente es un especialista con instrucciones propias: sabe a qué se dedica, cómo hablar y cuándo pasarle el tema a una
          persona. Elige una plantilla, ajústala a tu negocio y pruébalo en el chat.
        </p>
      </div>
      <TemplateGrid templates={TEMPLATES.slice(0, 3)} onPick={onPick} />
      <button type="button" onClick={onMore} className="mt-3 text-sm font-semibold text-primary hover:underline">
        Ver todas las plantillas o empezar desde cero →
      </button>
    </div>
  );
}

// ── Paso 1: plantilla ─────────────────────────────────────────

function TemplatePicker({ onPick, onBlank, onCancel }: {
  onPick: (t: AgentTemplate) => void;
  onBlank: () => void;
  onCancel: () => void;
}) {
  return (
    <section aria-labelledby="pick-title">
      <StepHeader step={1} title="¿Qué quieres que haga tu agente?" id="pick-title" onCancel={onCancel}>
        Elige una plantilla y la ajustas en el siguiente paso, o empieza desde cero.
      </StepHeader>
      <TemplateGrid templates={TEMPLATES} onPick={onPick} />
      <button
        type="button"
        onClick={onBlank}
        className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-border px-4 py-3 text-sm font-medium text-foreground hover:border-primary/40 hover:bg-muted/50"
      >
        <Sparkles size={15} aria-hidden className="text-primary" /> Empezar desde cero
      </button>
    </section>
  );
}

function TemplateGrid({ templates, onPick }: { templates: AgentTemplate[]; onPick: (t: AgentTemplate) => void }) {
  return (
    <ul className="grid auto-rows-fr grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {templates.map((t) => (
        <li key={t.key}>
          <button
            type="button"
            onClick={() => onPick(t)}
            className="orion-card group flex h-full w-full items-start gap-3 rounded-2xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40"
          >
            <span className={`orion-tone tone-${t.tone} grid h-10 w-10 shrink-0 place-items-center rounded-xl`} aria-hidden>
              {t.icon}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-foreground">{t.title}</span>
              <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">{t.summary}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ── Paso 2: ajustar / editar ──────────────────────────────────

function AgentEditor({ initial, fromTemplate, onBack, onCancel, onSaved }: {
  initial: Draft;
  fromTemplate?: string;
  onBack?: () => void;
  onCancel: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [d, setD] = useState<Draft>(initial);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const editing = !!initial.id;

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }));
  const canSave = d.name.trim().length > 0 && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    const body = {
      name: d.name.trim(),
      // Strings vacíos a propósito: al editar, borrar un campo debe guardarse.
      icon: d.icon.trim(),
      description: d.description.trim(),
      instructions: d.instructions.trim(),
      visibility: d.visibility,
    };
    try {
      if (editing) await updateAssistant(initial.id!, body);
      else await createAssistant(body);
      await onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar');
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="edit-title">
      <StepHeader
        step={editing ? undefined : 2}
        title={editing ? `Editar «${initial.name}»` : 'Ajusta tu agente'}
        id="edit-title"
        onBack={onBack}
        onCancel={onCancel}
      >
        {fromTemplate ? `Partiste de la plantilla «${fromTemplate}». Cámbiale lo que necesites.` : 'Dale un nombre y dile qué hacer.'}
      </StepHeader>

      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid grid-cols-[4.5rem_1fr] gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Ícono</span>
            <input
              value={d.icon}
              onChange={(e) => set('icon', e.target.value)}
              maxLength={4}
              placeholder="🤖"
              aria-label="Ícono (emoji)"
              className="h-11 w-full rounded-xl border border-input bg-card text-center text-xl text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <label className="block min-w-0">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Nombre</span>
            <input
              ref={nameRef}
              value={d.name}
              onChange={(e) => set('name', e.target.value)}
              maxLength={80}
              required
              placeholder="Ej. Cotizador de mobiliario"
              className="h-11 w-full rounded-xl border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">¿Para qué sirve? (una línea)</span>
          <input
            value={d.description}
            onChange={(e) => set('description', e.target.value)}
            maxLength={160}
            placeholder="Ej. Arma cotizaciones con nuestros precios y da seguimiento"
            className="h-10 w-full rounded-xl border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <label htmlFor="agent-instructions" className="text-xs font-medium text-muted-foreground">
              Instrucciones
            </label>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-controls="agent-instructions"
              aria-expanded={expanded}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-primary hover:bg-primary/10"
            >
              {expanded ? <Minimize2 size={12} aria-hidden /> : <Maximize2 size={12} aria-hidden />}
              {expanded ? 'Compactar' : 'Ampliar'}
            </button>
          </div>
          <textarea
            id="agent-instructions"
            value={d.instructions}
            onChange={(e) => set('instructions', e.target.value)}
            rows={expanded ? 16 : 4}
            placeholder="Cómo debe hablar, qué puede responder y qué debe pasar a una persona."
            className="w-full resize-y rounded-xl border border-input bg-card px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-foreground placeholder:font-sans placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <p className="mt-1 text-right text-[11px] tabular-nums text-muted-foreground">{d.instructions.length.toLocaleString('es-MX')} caracteres</p>
        </div>

        <fieldset>
          <legend className="mb-1 text-xs font-medium text-muted-foreground">¿Quién lo puede usar?</legend>
          <div role="radiogroup" className="inline-flex rounded-xl bg-muted p-1">
            {(['personal', 'org'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={d.visibility === v}
                onClick={() => set('visibility', v)}
                className={`inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium transition-colors ${
                  d.visibility === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {VISIBILITY[v].icon} {VISIBILITY[v].label}
              </button>
            ))}
          </div>
        </fieldset>

        {error && <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          <button type="button" onClick={onCancel} className="h-9 rounded-xl px-3 text-sm font-medium text-foreground hover:bg-muted">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSave}
            className="os-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-4 text-sm font-semibold"
          >
            {saving && <Loader2 size={15} className="animate-spin" aria-hidden />}
            {editing ? 'Guardar cambios' : 'Crear agente'}
          </button>
        </div>
      </form>
    </section>
  );
}

function StepHeader({ step, title, id, children, onBack, onCancel }: {
  step?: number;
  title: string;
  id: string;
  children?: React.ReactNode;
  onBack?: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mb-4 flex items-start gap-3">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Regresar a las plantillas"
          className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ArrowLeft size={16} aria-hidden />
        </button>
      )}
      <div className="min-w-0 flex-1">
        {step && <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">Paso {step} de 2</p>}
        <h2 id={id} className="text-lg font-semibold tracking-tight text-foreground">{title}</h2>
        {children && <p className="mt-0.5 text-sm text-muted-foreground">{children}</p>}
      </div>
      <button type="button" onClick={onCancel} className="h-8 shrink-0 rounded-lg px-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
        Cancelar
      </button>
    </div>
  );
}

export default AgentsApp;
